import { forwardRef, type ForwardedRef, type ReactNode } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import {
    buildRhfRules,
    mergeRefs,
    warnAboutRegisterSpread,
} from '../../_utils/rhf-ref';
import { type TreeSelect as PrimeTreeSelect } from 'primereact/treeselect';
import { TreeSelectInput } from './components/TreeSelectInput';
import { useTreeSelectCanonicalFieldValue } from './hooks/useTreeSelectCanonicalFieldValue';
import { type TreeSelectProps } from './models/tree-select.types';
import { InputWrapper } from '../InputWrapper/InputWrapper';

interface RHFTreeSelectProps extends TreeSelectProps {
    name: string;
    forwardedRef?: ForwardedRef<PrimeTreeSelect>;
}

function RHFTreeSelect(props: RHFTreeSelectProps): ReactNode {
    const { checkboxSelectionBehavior, name, options, selectionMode } = props;
    const formContext = useFormContext();

    useTreeSelectCanonicalFieldValue({
        checkboxSelectionBehavior,
        formContext,
        name,
        options,
        selectionMode,
    });

    return (
        <Controller
            name={name}
            control={formContext.control}
            rules={buildRhfRules({
                bulkMode: props.bulkMode,
                required: props.required,
                validate: props.validate,
            })}
            render={({ field, fieldState }) => (
                <InputWrapper {...props} field={field} fieldState={fieldState}>
                    <TreeSelectInput
                        {...props}
                        appendTo={document.body}
                        forwardedRef={mergeRefs(props.forwardedRef, field.ref)}
                        invalid={fieldState.invalid || props.invalid}
                        name={name}
                        value={field.value}
                        disabled={field.disabled || props.disabled}
                        onBlur={(event) => {
                            field.onBlur();
                            props.onBlur?.(event);
                        }}
                        onChange={(event) => {
                            field.onChange(event);
                            props.onChange?.(event);
                        }}
                    />
                </InputWrapper>
            )}
        />
    );
}

function TreeSelectComponent(
    props: TreeSelectProps,
    ref: ForwardedRef<PrimeTreeSelect>,
): ReactNode {
    const { name, value } = props;
    const formContext = useFormContext();
    const isControlled = typeof value !== 'undefined';
    const isRHFControlled = !isControlled && Boolean(formContext && name);
    warnAboutRegisterSpread({
        componentName: 'TreeSelect',
        isRHFControlled,
        onChange: props.onChange,
        ref,
    });

    if (!isRHFControlled) {
        return (
            <InputWrapper {...props}>
                <TreeSelectInput
                    {...props}
                    forwardedRef={ref}
                    invalid={Boolean(props.errorMessage || props.invalid)}
                />
            </InputWrapper>
        );
    }

    return <RHFTreeSelect {...props} name={name!} forwardedRef={ref} />;
}

export const TreeSelect = forwardRef(TreeSelectComponent);

TreeSelect.displayName = 'TreeSelect';

export type { TreeNode } from 'primereact/treenode';
export type {
    TreeSelectChangeEvent,
    TreeSelectCheckboxSelectionBehavior,
    TreeSelectProps,
    TreeSelectSelectedValueLayout,
} from './models/tree-select.types';
export type { TreeSelectValue } from './models/tree-select-checkbox-selection-behavior';
